import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants/item-labels',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/labels',
        destPath: '/v1/labels',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-labels',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_item-labels';

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item label.
 */
export function createLabel(payload: object): RequestOptionsFactory<any> {
  function __createLabel({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'label.createdDate' },
          { path: 'label.updatedDate' },
          { path: 'label.icon.urlExpirationDate' },
          { path: 'label.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.CreateLabel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'label.createdDate' },
              { path: 'label.updatedDate' },
              { path: 'label.icon.urlExpirationDate' },
              { path: 'label.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createLabel;
}

/**
 * > **Note:** The Labels API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item label by ID.
 */
export function getLabel(payload: object): RequestOptionsFactory<any> {
  function __getLabel({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.GetLabel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels/{labelId}', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'label.createdDate' },
              { path: 'label.updatedDate' },
              { path: 'label.icon.urlExpirationDate' },
              { path: 'label.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLabel;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 item labels.
 */
export function listLabels(payload: object): RequestOptionsFactory<any> {
  function __listLabels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.ListLabels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'labels.createdDate' },
              { path: 'labels.updatedDate' },
              { path: 'labels.icon.urlExpirationDate' },
              { path: 'labels.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listLabels;
}

/**
 * Creates a query to retrieve a list of item labels.
 *
 * The `queryLabels()` function builds a query to retrieve a list of item labels and returns a `LabelsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-labels/labels-query-builder/find) function.
 *
 * You can refine the query by chaining `LabelsQueryBuilder` functions onto the query. `LabelsQueryBuilder` functions enable you to filter, sort, and control the results that `queryLabels()` returns.
 *
 * `queryLabels()` runs with the following `LabelsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(100)`](/item-labels/labels-query-builder/limit)
 *
 * The following `LabelsQueryBuilder` functions are supported for `queryLabels()`. For a full description of the item label object, see the object returned for the [`items`](/item-labels/labels-query-result/items) property in `LabelsQueryResult`.
 */
export function queryLabels(payload: object): RequestOptionsFactory<any> {
  function __queryLabels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.QueryLabels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels/query', data: payload, host }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'labels.createdDate' },
              { path: 'labels.updatedDate' },
              { path: 'labels.icon.urlExpirationDate' },
              { path: 'labels.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryLabels;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of labels that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all labels.
 */
export function countLabels(payload: object): RequestOptionsFactory<any> {
  function __countLabels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.CountLabels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels/count', data: payload, host }
      ),
      data: payload,
    };

    return metadata;
  }

  return __countLabels;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item Label.
 *
 * Each time an item label is updated, its revision increments by 1. The existing revision must be included when updating the item labels. This ensures you're working with the latest item labels information, and it prevents unintended overwrites.
 */
export function updateLabel(payload: object): RequestOptionsFactory<any> {
  function __updateLabel({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'label.createdDate' },
          { path: 'label.updatedDate' },
          { path: 'label.icon.urlExpirationDate' },
          { path: 'label.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.UpdateLabel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels/{label.id}', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'label.createdDate' },
              { path: 'label.updatedDate' },
              { path: 'label.icon.urlExpirationDate' },
              { path: 'label.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLabel;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes an item label.
 */
export function deleteLabel(payload: object): RequestOptionsFactory<any> {
  function __deleteLabel({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.DeleteLabel',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/labels/{labelId}', data: payload, host }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteLabel;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple item labels at once.
 */
export function bulkDeleteLabels(payload: object): RequestOptionsFactory<any> {
  function __bulkDeleteLabels({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_label',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.BulkDeleteLabels',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressRestaurantsMenusItemLabelsV1RestaurantsMenusItemLabelsUrl(
        { protoPath: '/v1/bulk/labels/delete', data: payload, host }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __bulkDeleteLabels;
}
